import {
  clearAuthSession,
  getAccessToken,
  getRefreshToken,
  setAuthSession,
  updateOperator,
  type LoginResponse,
  type Operator,
} from './auth';
import { API_BASE } from './api-base';

export class ApiError extends Error {
  constructor(message: string, public status: number, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

let refreshInFlight: Promise<void> | null = null;

async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError('Invalid JSON response', res.status, text);
  }
}

async function refreshSession(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearAuthSession();
    throw new ApiError('Session expired', 401);
  }
  const res = await fetch(`${API_BASE}/admin/v2/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!res.ok) {
    clearAuthSession();
    throw new ApiError('Session expired', 401, await parseJson(res));
  }
  setAuthSession(await parseJson<LoginResponse>(res));
}

async function ensureRefreshed(): Promise<void> {
  if (!refreshInFlight) refreshInFlight = refreshSession().finally(() => { refreshInFlight = null; });
  await refreshInFlight;
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
  skipAuth?: boolean;
  retry?: boolean;
};

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipAuth = false, retry = true, headers, ...rest } = options;
  const reqHeaders = new Headers(headers);
  if (body !== undefined) reqHeaders.set('Content-Type', 'application/json');
  if (!skipAuth) {
    const token = getAccessToken();
    if (token) reqHeaders.set('Authorization', `Bearer ${token}`);
  }
  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: reqHeaders,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 401 && !skipAuth && retry) {
    await ensureRefreshed();
    return apiRequest<T>(path, { ...options, retry: false });
  }
  if (!res.ok) {
    const payload = await parseJson<unknown>(res);
    const raw = typeof payload === 'object' && payload !== null && 'message' in payload
      ? (payload as { message?: unknown }).message
      : undefined;
    const message = typeof raw === 'string' ? raw : `Request failed (${res.status})`;
    throw new ApiError(message, res.status, payload);
  }
  return parseJson<T>(res);
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const data = await apiRequest<LoginResponse>('/admin/v2/auth/login', {
    method: 'POST', body: { email, password }, skipAuth: true,
  });
  setAuthSession(data);
  return data;
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  try {
    await apiRequest('/admin/v2/auth/logout', {
      method: 'POST', body: refreshToken ? { refresh_token: refreshToken } : {},
    });
  } finally {
    clearAuthSession();
  }
}

export async function fetchMe(): Promise<Operator> {
  const data = await apiRequest<{ operator: Operator }>('/admin/v2/auth/me');
  updateOperator(data.operator);
  return data.operator;
}
